import {
  Box,
  Divider,
  Flex,
  FlexProps,
  forwardRef,
  Button,
  Link,
  LinkBox,
  LinkOverlay,
  Text,
  LinkOverlayProps,
} from '@chakra-ui/react';
import { isFuture } from 'date-fns';
import NextLink from 'next/link';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { LectureCardImage } from '@/components/ui/shared/lecture/LectureCardImage/LectureCardImage';
import { LectureSearchCardPopover } from '@/components/ui/shared/lecture/LectureSearchCard/components/LectureSearchCardPopover';
import { LectureSearchCardUpcomingPlaceholder } from '@/components/ui/shared/lecture/LectureSearchCard/components/LectureSearchCardUpcomingPlaceholder';
import { TagHighlight } from '@/components/ui/shared/search/TagHighlight';
import { ThinLecture } from '@/resources/ThinLecture';
import { LECTURE_CARD_IMAGE_FALLBACK } from '@/utils/constants';

import { LectureSearchCardAuthors } from './LectureSearchCardAuthors';
import { LectureSearchCardDate } from './LectureSearchCardDate';
import { LectureSearchCardTitle } from './LectureSearchCardTitle';

export interface IPosterLectureSearchCardProps extends FlexProps {
  lecture: ThinLecture;
  eventId: string;
  linkOverlayProps?: LinkOverlayProps;
}

export const PosterLectureSearchCard: FC<IPosterLectureSearchCardProps> = forwardRef<
  IPosterLectureSearchCardProps,
  'div'
>((props, ref) => {
  const { eventId, lecture, linkOverlayProps, ...rest } = props;

  const {
    publishedAt,
    mediumPosterDocumentThumbnailUrl,
    mediumPosterDocumentThumbnailExtractUrl,
    metadata: highlightedResults,
    linkSlug,
  } = lecture;

  const isUpcomingHeader = !publishedAt || isFuture(new Date(publishedAt));

  const { t } = useTranslation();

  const href = `/events/${eventId}/posters/?searchGroup=lecture&eventSessionId=${lecture?.eventSession?.id}`;
  const hrefLecture = `/events/${eventId}/posters/${lecture?.eventSession?.id}/poster/${linkSlug}`;
  const imageSrc = mediumPosterDocumentThumbnailUrl || mediumPosterDocumentThumbnailExtractUrl;

  return (
    <Flex
      as={LinkBox}
      zIndex={0}
      flexDir="column"
      _hover={{ textDecor: 'none' }}
      bg="white"
      flexBasis="290px"
      ref={ref}
      {...rest}
    >
      {isUpcomingHeader ? (
        <LectureSearchCardUpcomingPlaceholder publishedAt={publishedAt} />
      ) : (
        <LectureCardImage src={imageSrc || LECTURE_CARD_IMAGE_FALLBACK} alt={lecture.title} />
      )}
      <Flex direction="column" p={4} alignItems="flex-start" w="full" h="full">
        <LinkOverlay href={isUpcomingHeader || !lecture?.eventSession?.id ? null : hrefLecture} {...linkOverlayProps}>
          <LectureSearchCardTitle>
            {highlightedResults?.titleEn?.value ? (
              <TagHighlight htmlString={highlightedResults.titleEn.value} />
            ) : (
              lecture.title
            )}
          </LectureSearchCardTitle>
        </LinkOverlay>
        <Divider borderColor="gray.100" my={4} />
        <LectureSearchCardAuthors
          data={lecture?.positionSortedProfiles}
          highlightedResults={highlightedResults}
          noOfLines={5}
          minHeight="100px"
          href={isUpcomingHeader || !lecture?.eventSession?.id ? null : hrefLecture}
        />
        {lecture?.gatherTownUrl && (
          <Button
            as="a"
            my={2}
            href={lecture?.gatherTownUrl}
            target="_blank"
            size="sm"
            w="192px"
            display="flex"
            justifyContent="space-between"
            colorScheme="black"
          >
            {t('gatherTown.buttonLabel')}
          </Button>
        )}
        <Divider borderColor="gray.100" my={2} />
        <Text textStyle="groteskXS" fontWeight="bold" color="gray.500" mb={1} textTransform="capitalize">
          {t('common.session')}:
        </Text>
        <LectureSearchCardPopover
          bodySlot={
            <Text textStyle="groteskXS" fontWeight="bold" color="gray.900">
              {lecture.eventSession?.name}
            </Text>
          }
          triggerSlot={
            <Box mb={2}>
              <NextLink href={href} passHref legacyBehavior>
                <Text as={Link} textStyle="groteskXS" fontWeight="bold" color="gray.900" noOfLines={3}>
                  {highlightedResults?.eventSession?.name?.value ? (
                    <TagHighlight htmlString={highlightedResults.eventSession.name.value} />
                  ) : (
                    lecture.eventSession?.name
                  )}
                </Text>
              </NextLink>
            </Box>
          }
        />
        <Flex marginTop="auto">{lecture.heldAt ? <LectureSearchCardDate heldAt={lecture.heldAt} /> : null}</Flex>
      </Flex>
    </Flex>
  );
});
